import React, { useState, useEffect, useRef, lazy, Suspense } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  collection,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  serverTimestamp,
  deleteDoc,
  deleteField,
} from "firebase/firestore";
import { db } from "../../firebase";
import "./QNAAdminPanel.css";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

// Lazy load navbars (relative to src/admin/)
const Navbar = lazy(() => import("./Navbar"));
const NavbarApp = lazy(() => import("./NavbarApp"));

/**
 * Normalizes Firestore data with flat dotted field paths (legacy format)
 * into proper nested objects for backward compatibility.
 */
/**
 * Safely formats a date value for display.
 * Handles Firestore Timestamps, ISO strings, and null/undefined.
 */
const formatDate = (value, options = {}) => {
  if (!value) return "";
  let date;
  if (typeof value === "object" && typeof value.toMillis === "function") {
    date = new Date(value.toMillis());
  } else if (typeof value === "object" && typeof value.seconds === "number") {
    date = new Date(value.seconds * 1000 + (value.nanoseconds || 0) / 1e6);
  } else {
    date = new Date(value);
  }
  if (isNaN(date.getTime())) return "";
  const defaults = {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  };
  return date.toLocaleDateString("en-US", { ...defaults, ...options });
};


const isEmptyHtml = (html) => {
  if (!html) return true;
  const clean = html.replace(/<[^>]*>/g, "").trim();
  return clean === "";
};

const normalizeFirestoreData = (data) => {
  if (!data || typeof data !== "object" || Array.isArray(data)) return data;
  const hasDottedKeys = Object.keys(data).some((key) => key.includes("."));
  if (!hasDottedKeys) return data;
  const result = {};
  Object.entries(data).forEach(([key, value]) => {
    if (key.includes(".")) {
      const parts = key.split(".");
      let current = result;
      for (let i = 0; i < parts.length - 1; i++) {
        if (current[parts[i]] == null || typeof current[parts[i]] !== "object") {
          current[parts[i]] = {};
        }
        current = current[parts[i]];
      }
      current[parts[parts.length - 1]] = value;
    } else {
      result[key] = value;
    }
  });
  return result;
};

const AdminPanel = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const NavbarComponent = location.pathname.includes("/app") ? NavbarApp : Navbar;

  const orgCode = localStorage.getItem("orgCode") ;
  const adminStudentId = localStorage.getItem("studentId");
  const [companyName, setCompanyName] = useState("");
  const [questions, setQuestions] = useState([]);
  const [adminData, setAdminData] = useState(null);
  const [isVerified, setIsVerified] = useState(false);
  const [fetchingQuestions, setFetchingQuestions] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verifyError, setVerifyError] = useState(null);
  const [answerTexts, setAnswerTexts] = useState({});
  const [submitting, setSubmitting] = useState({});
  const [filter, setFilter] = useState("all");
  const [selectedCourse, setSelectedCourse] = useState("all");
  const [courseList, setCourseList] = useState(["General"]);
  const [editorFocused, setEditorFocused] = useState({});
  const [editingAnswerId, setEditingAnswerId] = useState(null);
  const [editingAnswerText, setEditingAnswerText] = useState("");

  // Fetch company name on mount
  useEffect(() => {
    const fetchCompanyName = async () => {
      if (!orgCode) return;
      try {
        const companyRef = doc(db, "CorporateClients", orgCode);
        const companySnap = await getDoc(companyRef);
        if (companySnap.exists()) {
          setCompanyName(companySnap.data().companyName || orgCode);
        } else {
          setCompanyName(orgCode);
        }
      } catch (err) {
        console.error("Error fetching company name:", err);
        setCompanyName(orgCode);
      }
    };
    fetchCompanyName();
  }, [orgCode]);

  // Auto-verify admin on mount
  useEffect(() => {
    if (adminStudentId) {
      verifyAdmin(adminStudentId);
    }
  }, []);

  // Verify admin when a student is selected
  const verifyRef = useRef(null);

  const verifyAdmin = async (studentId) => {
    if (!studentId || !orgCode) {
      setIsVerified(false);
      setAdminData(null);
      setVerifyError(null);
      setVerifying(false);
      return;
    }

    const thisRequest = Symbol();
    verifyRef.current = thisRequest;
    setVerifying(true);
    setVerifyError(null);

    try {
      const studentRef = doc(db, `CorporateClients/${orgCode}/studentInfo/${studentId}`);
      const snap = await getDoc(studentRef);

      if (verifyRef.current !== thisRequest) return;

      if (!snap.exists()) {
        setVerifyError("Student not found in the system.");
        setIsVerified(false);
        setAdminData(null);
        return;
      }

      if (snap.data().isAdmin !== true) {
        setVerifyError("Access denied. This student does not have admin privileges.");
        setIsVerified(false);
        setAdminData(null);
        return;
      }

      const fullName = snap.data().fullName || studentId;
      localStorage.setItem("isAdmin", "true");
      setAdminData({
        id: studentId,
        fullName,
      });
      setIsVerified(true);
      setVerifyError(null);
      fetchAllQuestions();
    } catch (err) {
      if (verifyRef.current !== thisRequest) return;
      console.error("Failed to verify admin:", err);
      setVerifyError("Network error. Please check your connection and try again.");
      setIsVerified(false);
      setAdminData(null);
    } finally {
      if (verifyRef.current === thisRequest) {
        setVerifying(false);
      }
    }
  };

  // Fetch ALL questions from ALL students
  // Supports TWO formats:
  //   NEW: course subcollection — questionanswer/{studentId}/{courseName}/{questionId}
  //   LEGACY: flat doc — questionanswer/{studentId} { courses: { Course01: { questions: { qId: {...} } } } }
  const fetchAllQuestions = async () => {
    if (!orgCode) return;
    setFetchingQuestions(true);
    try {
      const allQuestions = [];
      const loadedIds = new Set();

      // First, get course names from programInfo (for new-format querying)
      let knownCourses = ["General"];
      try {
        const programInfoRef = collection(db, `CorporateClients/${orgCode}/programInfo`);
        const programSnapshot = await getDocs(programInfoRef);
        const programCourses = [];
        programSnapshot.forEach((d) => {
          if (!/capstone/i.test(d.id)) programCourses.push(d.id);
        });
        knownCourses = [...knownCourses, ...programCourses];
        setCourseList(knownCourses);
      } catch {
        // programInfo may not exist — use just "General"
      }

      const studentMap = {};

      // ── Populate studentMap with all students from studentInfo ──
      try {
        const studentsRef = collection(db, `CorporateClients/${orgCode}/studentInfo`);
        const studentsSnapshot = await getDocs(studentsRef);
        studentsSnapshot.forEach((sDoc) => {
          const sid = sDoc.id;
          const studentName = sDoc.data().fullName || sid;
          studentMap[sid] = { studentName, courseNames: new Set() };
        });
      } catch (err) {
        console.warn("Failed to fetch students from studentInfo:", err);
      }

      // ── Read all student docs from questionanswer (covers legacy data) ──
      const qaRef = collection(db, `CorporateClients/${orgCode}/questionanswer`);
      const qaSnapshot = await getDocs(qaRef);

      qaSnapshot.forEach((studentDoc) => {
        const sid = studentDoc.id;
        const rawData = studentDoc.data();
        const data = normalizeFirestoreData(rawData);
        const studentName = data.studentName || (studentMap[sid]?.studentName) || sid;

        if (!studentMap[sid]) {
          studentMap[sid] = { studentName, courseNames: new Set() };
        }

        // ── LEGACY: parse nested courses.questions map ──
        if (data.courses) {
          Object.entries(data.courses).forEach(([courseName, courseData]) => {
            studentMap[sid].courseNames.add(courseName);
            if (courseData && courseData.questions) {
              Object.entries(courseData.questions).forEach(([qId, qData]) => {
                if (!loadedIds.has(qId)) {
                  loadedIds.add(qId);
                  allQuestions.push({
                    id: qId,
                    studentId: sid,
                    studentName,
                    category: courseName,
                    ...qData,
                  });
                }
              });
            }
          });
        }
      });

      // ── NEW FORMAT: query each course subcollection for each student ──
      const subcollectionPromises = [];
      Object.entries(studentMap).forEach(([sid, info]) => {
        const coursesToCheck = new Set([...info.courseNames, ...knownCourses]);
        coursesToCheck.forEach((courseName) => {
          const questionsRef = collection(
            db,
            `CorporateClients/${orgCode}/questionanswer/${sid}/${courseName}`
          );
          subcollectionPromises.push(
            getDocs(questionsRef).then((snapshot) => {
              snapshot.forEach((qDoc) => {
                const data = qDoc.data();
                const qId = qDoc.id;
                if (!loadedIds.has(qId)) {
                  loadedIds.add(qId);
                  allQuestions.push({
                    id: qId,
                    studentId: sid,
                    studentName: info.studentName,
                    category: courseName,
                    question: data.question || "",
                    answer: data.answer || null,
                    answeredBy: data.answeredBy || null,
                    answeredAt: data.answeredAt || null,
                    createdAt: data.createdAt || null,
                    status: data.status || "pending",
                  });
                }
              });
            })
          );
        });
      });

      await Promise.all(subcollectionPromises);

      // Sort by createdAt descending
      allQuestions.sort((a, b) => {
        const aTime = a.createdAt?.toMillis
          ? a.createdAt.toMillis()
          : new Date(a.createdAt || 0).getTime();
        const bTime = b.createdAt?.toMillis
          ? b.createdAt.toMillis()
          : new Date(b.createdAt || 0).getTime();
        return bTime - aTime;
      });

      setQuestions(allQuestions);
    } catch (err) {
      console.error("Failed to fetch questions:", err);
    } finally {
      setFetchingQuestions(false);
    }
  };

  // Handle answer submission
  // Saves to BOTH formats for full compatibility:
  //   NEW: course subcollection — questionanswer/{studentId}/{courseName}/{questionId}
  //   LEGACY: flat doc with dotted keys — questionanswer/{studentId}
  const handleSubmitAnswer = async (q) => {
    if (isEmptyHtml(answerTexts[q.id]) || !adminData || !orgCode) return;

    setSubmitting((prev) => ({ ...prev, [q.id]: true }));

    const answerText = answerTexts[q.id];
    const answeredBy = adminData.fullName;
    let newFormatSuccess = false;
    let legacyFormatSuccess = false;

    // ── NEW FORMAT: update the specific question document in the course subcollection ──
    // Path: questionanswer/{studentId}/{category}/{questionId}
    try {
      const questionDocRef = doc(
        db,
        `CorporateClients/${orgCode}/questionanswer/${q.studentId}/${q.category}/${q.id}`
      );
      await updateDoc(questionDocRef, {
        answer: answerText,
        answeredBy,
        answeredAt: serverTimestamp(),
        status: "answered",
      });
      newFormatSuccess = true;
    } catch {
      // Doc may not exist yet (legacy question) — will be saved via legacy path
    }

    // ── LEGACY FORMAT: dotted-path fields on the student doc ──
    try {
      const legacyDocRef = doc(
        db,
        `CorporateClients/${orgCode}/questionanswer/${q.studentId}`
      );
      await updateDoc(legacyDocRef, {
        [`courses.${q.category}.questions.${q.id}.answer`]: answerText,
        [`courses.${q.category}.questions.${q.id}.answeredBy`]: answeredBy,
        [`courses.${q.category}.questions.${q.id}.answeredAt`]: serverTimestamp(),
        [`courses.${q.category}.questions.${q.id}.status`]: "answered",
      });
      legacyFormatSuccess = true;
    } catch {
      // Legacy doc may not exist for new-format-only questions
    }

    if (!newFormatSuccess && !legacyFormatSuccess) {
      console.error("Failed to submit answer: both new and legacy formats failed");
      alert("Failed to save answer. Please try again.");
      setSubmitting((prev) => ({ ...prev, [q.id]: false }));
      return;
    }

    // Update local state immediately
    setQuestions((prev) =>
      prev.map((item) =>
        item.id === q.id
          ? {
            ...item,
            answer: answerText,
            answeredBy,
            answeredAt: new Date().toISOString(),
            status: "answered",
          }
          : item
      )
    );

    setAnswerTexts((prev) => ({ ...prev, [q.id]: "" }));
    setSubmitting((prev) => ({ ...prev, [q.id]: false }));
  };

  
const handleUpdateAnswer = async (q) => {
  if (isEmptyHtml(editingAnswerText)) return;
  const updatedAnswer = editingAnswerText;
  try {
    try {
      const questionDocRef = doc(
        db,
        `CorporateClients/${orgCode}/questionanswer/${q.studentId}/${q.category}/${q.id}`
      );
      await updateDoc(questionDocRef,{
        answer:updatedAnswer,
        answeredBy:adminData.fullName,
        answeredAt:serverTimestamp(),
        status:"answered"
      });
    } catch {}
    try{
      const legacyDocRef=doc(db,`CorporateClients/${orgCode}/questionanswer/${q.studentId}`);
      await updateDoc(legacyDocRef,{
        [`courses.${q.category}.questions.${q.id}.answer`]:updatedAnswer,
        [`courses.${q.category}.questions.${q.id}.answeredBy`]:adminData.fullName,
        [`courses.${q.category}.questions.${q.id}.answeredAt`]:serverTimestamp(),
        [`courses.${q.category}.questions.${q.id}.status`]:"answered",
      });
    }catch{}
    setQuestions(prev=>prev.map(item=>item.id===q.id?{...item,answer:updatedAnswer,answeredBy:adminData.fullName,answeredAt:new Date().toISOString()}:item));
    setEditingAnswerId(null);
    setEditingAnswerText("");
  } catch(err){
    console.error(err);
  }
};

const handleDeleteQuestion = async (q) => {
    if (!window.confirm("Are you sure you want to delete this question? This action cannot be undone.")) {
      return;
    }

    let newFormatSuccess = false;
    let legacyFormatSuccess = false;

    // ── NEW FORMAT: delete the specific question document from the course subcollection ──
    try {
      const questionDocRef = doc(
        db,
        `CorporateClients/${orgCode}/questionanswer/${q.studentId}/${q.category}/${q.id}`
      );
      await deleteDoc(questionDocRef);
      newFormatSuccess = true;
    } catch (err) {
      console.warn("Failed to delete question from new format collection:", err);
    }

    // ── LEGACY FORMAT: remove the question key from the student document ──
    try {
      const legacyDocRef = doc(
        db,
        `CorporateClients/${orgCode}/questionanswer/${q.studentId}`
      );
      await updateDoc(legacyDocRef, {
        [`courses.${q.category}.questions.${q.id}`]: deleteField(),
      });
      legacyFormatSuccess = true;
    } catch (err) {
      console.warn("Failed to delete question from legacy format map:", err);
    }

    if (!newFormatSuccess && !legacyFormatSuccess) {
      console.error("Failed to delete question: both new and legacy formats failed");
      alert("Failed to delete question. Please try again.");
      return;
    }

    // Update local state immediately
    setQuestions((prev) => prev.filter((item) => item.id !== q.id));
    alert("Question deleted successfully.");
  };

  // Filter questions by status and selected course
  const filteredQuestions = questions.filter((q) => {
    const statusMatch = filter === "all" || q.status === filter;
    const courseMatch = selectedCourse === "all" || q.category === selectedCourse;
    return statusMatch && courseMatch;
  });

  return (
    <>
      <Suspense fallback={<div>Loading Navbar...</div>}>
        <NavbarComponent companyName={companyName} />
      </Suspense>

      <div className="ap-page">
        <div className="ap-content">
          {verifying ? (
            <div className="ap-loading">
              <div className="ap-spinner"></div>
              <p>Verifying admin access...</p>
            </div>
          ) : !isVerified ? (
            <div className="ap-access-denied">
              <svg className="ap-denied-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
              </svg>
              <h2>Access Denied</h2>
              <p>
                {verifyError || (
                  <>
                    <strong>{adminStudentId}</strong> does not have admin privileges.
                    Only users with <code>isAdmin: true</code> can access this panel.
                  </>
                )}
              </p>
              <p className="ap-retry-note">
                Only users with admin privileges can access this panel.
              </p>
            </div>
          ) : (
            <>
              {/* Course filter dropdown */}
              <div className="ap-course-filter-container" style={{ marginBottom: "20px", display: "flex", alignItems: "center", gap: "10px", backgroundColor: "#fff", padding: "12px 18px", borderRadius: "10px", boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}>
                <label style={{ fontWeight: "600", color: "#475569" }}>Select Course:</label>
                <select
                  value={selectedCourse}
                  onChange={(e) => setSelectedCourse(e.target.value)}
                  style={{ padding: "8px 12px", borderRadius: "8px", border: "1.5px solid #cbd5e1", outline: "none", fontSize: "0.95rem", minWidth: "150px" }}
                >
                  <option value="all">All Courses</option>
                  {courseList.map((course) => (
                    <option key={course} value={course}>
                      {course}
                    </option>
                  ))}
                </select>
              </div>

            {fetchingQuestions && (
              <div className="ap-loading">Loading questions...</div>
            )}

            {!fetchingQuestions && questions.length === 0 && (
              <div className="ap-empty-state">
                <svg className="ap-empty-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p className="ap-empty-title">No questions have been asked yet</p>
                <p className="ap-empty-subtitle">Questions will appear here once students ask them.</p>
              </div>
            )}

            {/* Filter buttons */}
            {!fetchingQuestions && questions.length > 0 && (
              <>
                <div className="ap-filter-bar">
                  <div className="ap-filter-group">
                    {["all", "pending", "answered"].map((f) => (
                      <button
                        key={f}
                        onClick={() => setFilter(f)}
                        className={`ap-filter-btn ${filter === f ? "ap-filter-btn-active" : ""}`}
                      >
                        {f.charAt(0).toUpperCase() + f.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="ap-questions-list">
                  {filteredQuestions.map((q) => (
                    <div
                      key={q.id}
                      className={`ap-question-card ${q.status === "answered" ? "ap-card-answered" : "ap-card-pending"
                        }`}
                    >
                      <div className="ap-question-header">
                        <div className="ap-badges-container">
                          <div className="ap-badges-left">
                            <span
                              className={`ap-badge ${q.status === "answered" ? "ap-badge-answered" : "ap-badge-pending"
                                }`}
                            >
                              {q.status === "answered" ? "Answered" : "Pending"}
                            </span>
                            <span className="ap-badge ap-badge-category">{q.category}</span>
                          </div>
                          <button
                            onClick={() => handleDeleteQuestion(q)}
                            className="ap-delete-btn"
                            title="Delete Question"
                          >
                            <svg
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                              />
                            </svg>
                          </button>
                        </div>
                        <div
                          className="ap-question-text ap-rich-text"
                          dangerouslySetInnerHTML={{ __html: q.question }}
                        />
                        <div className="ap-question-meta">
                          <span className="ap-meta-student">
                            Asked by <strong>{q.studentName}</strong>
                          </span>
                          <span className="ap-meta-date">
                            &middot;{" "}
                            {formatDate(q.createdAt)}
                          </span>
                        </div>
                      </div>

                      {/* Answer section */}
                      <div className="ap-answer-section">
                        {q.status === "answered" ? (
                          <div className="ap-answer-display">
                            <div className="ap-answer-inner">
                              <div className="ap-answer-avatar">
                                {q.answeredBy?.charAt(0).toUpperCase() || "A"}
                              </div>
                              <div className="ap-answer-body">
                                <div className="ap-answer-meta">
                                  <span className="ap-answer-author">{q.answeredBy}</span>
                                  <span className="ap-answer-date">
                                    &middot;{" "}
                                    {formatDate(q.answeredAt)}
                                  </span>
                                </div>
                                
{editingAnswerId === q.id ? (
  <>
    <ReactQuill
      theme="snow"
      value={editingAnswerText}
      onChange={setEditingAnswerText}
    />
    <div style={{display:"flex",gap:"10px",marginTop:"12px"}}>
      <button
        className="ap-submit-answer-btn"
        onClick={() => handleUpdateAnswer(q)}
        disabled={isEmptyHtml(editingAnswerText)}
      >
        Save Changes
      </button>
      <button
        className="ap-delete-btn"
        onClick={()=>{
          setEditingAnswerId(null);
          setEditingAnswerText("");
        }}
      >
        Cancel
      </button>
    </div>
  </>
) : (
  <>
    <div
      className="ap-answer-text ap-rich-text"
      dangerouslySetInnerHTML={{ __html: q.answer }}
    />
    <button
      className="ap-submit-answer-btn"
      style={{marginTop:"12px"}}
      onClick={()=>{
        setEditingAnswerId(q.id);
        setEditingAnswerText(q.answer || "");
      }}
    >
      Edit Answer
    </button>
  </>
)}

                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="ap-answer-form">
                            <div className={`ap-answer-editor ${editorFocused[q.id] ? "focused" : ""}`}>
                              <ReactQuill
                                theme="snow"
                                value={answerTexts[q.id] || ""}
                                onChange={(value) =>
                                  setAnswerTexts((prev) => ({ ...prev, [q.id]: value }))
                                }
                                onFocus={() =>
                                  setEditorFocused((prev) => ({ ...prev, [q.id]: true }))
                                }
                                onBlur={() =>
                                  setEditorFocused((prev) => ({ ...prev, [q.id]: false }))
                                }
                                placeholder="Type your answer here..."
                              />
                            </div>
                            <button
                              onClick={() => handleSubmitAnswer(q)}
                              disabled={isEmptyHtml(answerTexts[q.id]) || submitting[q.id]}
                              className="ap-submit-answer-btn"
                            >
                              {submitting[q.id] ? "Submitting..." : "Submit Answer"}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
    </>
  );
};

export default AdminPanel;
